import * as React from "react";
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { cn } from "../lib/cn";
import { useAiElementsScope } from "../scope";

export type DialogProps = React.ComponentProps<typeof DialogPrimitive.Root>;
export type DialogTriggerProps = React.ComponentProps<typeof DialogPrimitive.Trigger>;
export type DialogCloseProps = React.ComponentProps<typeof DialogPrimitive.Close>;
export type DialogOverlayProps = React.ComponentProps<typeof DialogPrimitive.Overlay>;
export type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  closeLabel?: string;
  /** Fills the visual viewport instead of floating as a centered card. */
  fullScreen?: boolean;
};

/** Scoped wrapper for the upstream dialog root. */
export const Dialog = (props: DialogProps) => (
  <DialogPrimitive.Root data-slot="dialog" {...props} />
);
/** Scoped wrapper for the upstream dialog trigger. */
export const DialogTrigger = (props: DialogTriggerProps) => (
  <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
);
/** Scoped wrapper for the upstream dialog close control. */
export const DialogClose = (props: DialogCloseProps) => (
  <DialogPrimitive.Close data-slot="dialog-close" {...props} />
);
/** Dimming layer behind a {@link DialogContent}. */
export const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className,
    )}
    data-slot="dialog-overlay"
    {...props}
  />
);

/**
 * Dialog surface whose portal stays inside the AI Elements theme boundary.
 *
 * `closeLabel` exists because this package renders inside a localized host application: the
 * built-in close control needs an accessible name the caller can translate.
 */
/*
 * A centered card positions itself with `top-1/2` plus a translate, which measures against the
 * layout viewport. On mobile the dynamic viewport is shorter than that, so a `h-dvh` card sits
 * inset rather than edge to edge. The full-screen variant therefore drops the centering entirely
 * and pins all four edges.
 */
const DIALOG_FULL_SCREEN_CLASSNAME =
  "inset-0 h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-0 p-0 sm:max-w-none";

export const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  closeLabel = "Close",
  fullScreen = false,
  ...props
}: DialogContentProps) => {
  const container = useAiElementsScope();
  return (
    <DialogPrimitive.Portal container={container ?? undefined} data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg",
          fullScreen && DIALOG_FULL_SCREEN_CLASSNAME,
          className,
        )}
        data-full-screen={fullScreen ? "true" : undefined}
        data-slot="dialog-content"
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            data-slot="dialog-close"
          >
            <XIcon />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

export type DialogHeaderProps = React.ComponentProps<"div">;
/** Stacked title and description block for a dialog. */
export const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div
    className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
    data-slot="dialog-header"
    {...props}
  />
);
export type DialogFooterProps = React.ComponentProps<"div">;
/** Action row for a dialog. */
export const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    data-slot="dialog-footer"
    {...props}
  />
);
export type DialogTitleProps = React.ComponentProps<typeof DialogPrimitive.Title>;
/** Accessible name of a dialog. */
export const DialogTitle = ({ className, ...props }: DialogTitleProps) => (
  <DialogPrimitive.Title
    className={cn("text-lg leading-none font-semibold", className)}
    data-slot="dialog-title"
    {...props}
  />
);
export type DialogDescriptionProps = React.ComponentProps<typeof DialogPrimitive.Description>;
/** Supporting copy of a dialog. */
export const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => (
  <DialogPrimitive.Description
    className={cn("text-sm text-muted-foreground", className)}
    data-slot="dialog-description"
    {...props}
  />
);
